import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../services/api";
import { getUser } from "../utils/storage";
import { useToast } from "../components/GlobalToast";

const PlayerContests = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [joining, setJoining] = useState(null);
  const [prediction, setPrediction] = useState("");
  
  // Initialize local join counts from passed challenges
  const { match, category, player, challenges: initialChallenges } = location.state || {};
  const [challenges, setChallenges] = useState(initialChallenges || []);
  const [loading, setLoading] = useState(!initialChallenges || initialChallenges.length === 0);
  const [userJoinCounts, setUserJoinCounts] = useState(
    (challenges || []).reduce((acc, c) => ({ ...acc, [c.id]: Number(c.user_joined_count || 0) }), {})
  );

  React.useEffect(() => {
    if (!initialChallenges || initialChallenges.length === 0) {
      fetchChallenges();
    }
  }, []);

  const fetchChallenges = async () => {
    try {
      setLoading(true);
      const user = getUser();
      const matchId = match.match_id || match.id;
      const res = await API.get(`/challenges/${matchId}?userId=${user.id || 0}`);
      const fetchedChallenges = res.data.challenges || [];
      setChallenges(fetchedChallenges);
      setUserJoinCounts(
        fetchedChallenges.reduce((acc, c) => ({ ...acc, [c.id]: Number(c.user_joined_count || 0) }), {})
      );
    } catch (err) {
      console.error("Failed to fetch challenges in fallback:", err);
    } finally {
      setLoading(false);
    }
  };

  // Read passed data

  const getTieBreakerText = () => {
    const cId = category?.id;
    if (cId === 'MILESTONES') return "Predict exact runs he will score to win ties against other users.";
    if (cId === 'MOTM' || cId === 'COMBO') return "Predict his exact Dream11 Points to win ties against other users.";
    return `Predict exact ${category?.label?.replace('Most ', '')} he will hit/take to win ties.`;
  };

  // If directly accessed without state, redirect back
  if (!match || !player) {
    return (
      <div className="min-h-screen bg-theme-bg text-theme-text flex flex-col items-center justify-center">
        <p>Invalid Selection</p>
        <button onClick={() => navigate("/player-performance")} className="mt-4 px-4 py-2 bg-red-500 rounded text-sm font-bold">Go Back</button>
      </div>
    );
  }

  // Get all active tiers for this category from DB
  const activeChallenges = challenges?.filter(c => c.category === category.id && c.is_active !== false) || [];

  const contestsList = activeChallenges.map(c => ({
    id: c.id,
    entry: Number(c.entry_fee),
    win: Number(c.prize_pool),
    spots: c.total_spots || 100,
    filled: Math.min(c.total_spots || 100, (c.filled_spots || 0) + (c.fake_joined || 0)),
    name: c.title || "Contest",
  })).sort((a, b) => a.entry - b.entry);

  const handleJoin = async (contest) => {
    const user = getUser();
    if (!user.id) {
      toast.warn("Please login first to join challenges.");
      return;
    }

    // Check if user has already joined with THIS player to allow updates
    // In frontend we don't have the exact list of joined players in state easily,
    // so we rely on the backend to handle the update vs insert logic. 
    // The backend won't charge extra if it's an update.
    
    setJoining(contest.id);
    try {
      const res = await API.post("/challenges/join", {
        challenge_id: contest.id,
        user_id: user.id || 1,
        player_id: player.player_id,
        prediction: Number(prediction) || 0
      });
      
      toast.success(res.data.message || `Joined ${contest.name} for ${player.player_name}! 🏏`);
      
      // Update local join count
      setUserJoinCounts(prev => ({
        ...prev,
        [contest.id]: (prev[contest.id] || 0) + 1
      }));

      // User stays on page to join more contests

    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || "Insufficient wallet balance or server error.");
    } finally {
      setJoining(null);
    }
  };

  return (
    <div className="bg-theme-card text-theme-text min-h-screen pb-10 font-sans">
      
      {/* 🔙 TOP HEADER */}
      <div className="flex items-center p-4 sticky top-0 bg-theme-card/90 backdrop-blur-xl z-50 border-b border-theme-border">
        <button 
          onClick={() => navigate(-1)} 
          className="w-10 h-10 flex items-center justify-center bg-theme-bg rounded-xl border border-theme-border/50 text-theme-text transition-all active:scale-95"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-sm font-black uppercase tracking-widest text-theme-text-sec">Select Contest</h1>
          <p className="text-[10px] text-theme-text-sec font-bold uppercase">{match.short_title || "T20 Match"}</p>
        </div>
      </div>

      {/* 🏏 PLAYER HIGHLIGHT */}
      <div className="px-4 pt-6 pb-4">
        <div className="relative bg-theme-bg rounded-3xl p-5 border border-theme-border overflow-hidden shadow-theme-md">
          {/* subtle category color glow */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 blur-3xl rounded-full"></div>
          
          <div className="flex items-center space-x-4 relative z-10">
            <div className="w-16 h-16 bg-theme-card border border-theme-border rounded-full overflow-hidden border-2 border-theme-border shadow-lg shrink-0">
               <img src={player.image || 'https://via.placeholder.com/60'} alt={player.player_name} className="w-full h-full object-cover" onError={(e) => {e.target.style.display='none'}} />
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-0.5">
                <span className="bg-red-500 text-white text-[9px] px-2 py-0.5 rounded font-black uppercase tracking-wider">{category.label}</span>
                <span className="text-[10px] font-bold text-theme-text-sec capitalize">{player.role}</span>
              </div>
              <h2 className="text-xl font-black text-theme-text">{player.player_name}</h2>
              <p className="text-xs text-theme-text-sec mt-0.5 font-medium">Predicting {player.player_name} to hit {category.label.replace('Most ', '')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 🚀 TIE-BREAKER MAGIC QUESTION */}
      <div className="px-4 mt-2 mb-4">
        <div className="bg-theme-card border-2 border-red-500/20 rounded-2xl p-4 shadow-lg flex items-center justify-between">
           <div>
             <h4 className="text-sm font-black text-theme-text flex items-center gap-1">
               <span className="text-red-500 text-lg">💡</span> Tie-Breaker
             </h4>
             <p className="text-[10px] text-theme-text-sec font-bold max-w-[170px] mt-0.5 leading-tight">{getTieBreakerText()}</p>
           </div>
           <div className="flex items-center space-x-2 bg-theme-bg rounded-full border border-theme-border p-1">
             <button onClick={() => setPrediction(Math.max(0, (Number(prediction) || 0) - 1))} className="w-8 h-8 rounded-full bg-theme-card border border-theme-border hover:bg-theme-bg text-theme-text font-black text-lg flex items-center justify-center transition active:scale-95 shadow-inner">-</button>
             <input type="text" 
                    inputMode="numeric" 
                    pattern="[0-9]*"
                    className="text-lg font-black w-10 bg-transparent text-center text-red-400 outline-none" 
                    value={prediction} 
                    placeholder="0"
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setPrediction(val === "" ? "" : Math.max(0, Number(val)));
                    }} 
             />
             <button onClick={() => setPrediction((Number(prediction) || 0) + 1)} className="w-8 h-8 rounded-full bg-theme-card border border-theme-border hover:bg-theme-bg text-theme-text font-black text-lg flex items-center justify-center transition active:scale-95 shadow-inner">+</button>
           </div>
        </div>
      </div>

      {/* 🏆 CONTESTS LIST */}
      <div className="px-4 mt-2">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-bold text-theme-text-sec uppercase tracking-widest content-center">Available Contests</h3>
          <span className="text-[10px] bg-theme-card border border-theme-border text-theme-text-sec px-2 py-1 rounded font-bold">Sort by: Entry</span>
        </div>

        <div className="space-y-4">
          {loading ? (
             <div className="flex flex-col items-center justify-center py-20 gap-4">
               <div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
               <p className="text-[10px] font-black text-gray-600 uppercase tracking-widest">Loading Contests...</p>
             </div>
          ) : contestsList.length === 0 ? (
            <div className="text-center py-20 text-theme-text-sec font-bold uppercase tracking-widest text-xs bg-white/5 rounded-3xl">No contests available for this category.</div>
          ) : contestsList.map(contest => (
            <div key={contest.id} className="bg-theme-card rounded-2xl p-5 border border-theme-border shadow-2xl relative overflow-hidden group hover:scale-[1.01] transition-transform duration-300">
              
              {/* 🔝 Top Row: Title & Prize Pool */}
              <div className="flex justify-between items-center mb-6">
                <div className="flex-1">
                  <h2 className="text-xl font-black italic uppercase leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500">
                    {contest.name.split(' ').join('\n')}
                  </h2>
                </div>

                <div className="flex flex-col items-end">
                  <span className="text-[10px] font-black italic text-orange-500 uppercase tracking-widest mb-1">Prize Pool</span>
                  <div className="bg-gradient-to-r from-orange-600 to-orange-400 px-4 py-2 rounded-xl shadow-lg transform rotate-[-2deg]">
                    <p className="text-lg font-black text-theme-text italic">₹{contest.win.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* 🚀 Middle: Shining Green Join Button */}
              <button
                onClick={() => handleJoin(contest)}
                disabled={joining === contest.id}
                className="w-full bg-gradient-to-r from-[#00c853] to-[#64dd17] hover:brightness-105 py-3.5 rounded-full flex justify-center items-center gap-3 mb-4 shadow-[0_10px_30px_rgba(0,200,83,0.2)] active:scale-95 transition-all relative overflow-hidden group"
              >
                <div className="absolute inset-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-[45deg] translate-x-[-200%] group-hover:translate-x-[250%] transition-transform duration-1000 ease-in-out" />
                
                <span className="text-white font-black uppercase text-sm tracking-[0.2em] relative z-10 drop-shadow-md">
                  {joining === contest.id ? "JOINING..." : "JOIN "}
                </span>
                <div className="bg-white/20 backdrop-blur-sm px-3 py-0.5 rounded-xl border border-white/30 relative z-10">
                  <span className="text-white font-black text-xl italic drop-shadow-md">₹{contest.entry}</span>
                </div>
              </button>

              {/* 📊 Bottom: Personalized Tracker & Stats */}
              <div className="flex justify-between items-center text-[11px] font-bold text-theme-text-sec tracking-wide bg-theme-bg/30 p-2 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <span className="bg-theme-card border border-theme-border p-0.5 px-1 rounded text-[10px] border border-theme-border">M</span>
                  <span>{userJoinCounts[contest.id] || 0} / 5 Players</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {[1,2,3].map(i => (
                      <div key={i} className="w-5 h-5 rounded-full border-2 border-[#1a1310] bg-gray-700">
                        <img src={`https://i.pravatar.cc/30?u=${contest.id+i}`} className="w-full h-full rounded-full opacity-80" alt=""/>
                      </div>
                    ))}
                  </div>
                  <span className="text-theme-text">{contest.filled.toLocaleString()} / {contest.spots.toLocaleString()} Spots</span>
                </div>
              </div>

              {/* 🎨 Glow Decor */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-orange-500/10 blur-[50px] pointer-events-none" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlayerContests;
